import React from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { routeTree } from './routeTree.gen';
import './index.css';

// Theme — must run before React paints so there's no light-mode flash for
// users who picked dark (or whose OS is dark). Reads localStorage + OS
// preference, sets data-theme on <html>, subscribes to OS-preference changes.
import { initTheme } from './utils/theme';
initTheme();

// Sentry — initialise as early as possible so any subsequent error
// (including ones during route/auth setup below) gets captured.
// No-ops when VITE_SENTRY_DSN is unset.
import { initSentry } from './sentry';
import * as Sentry from '@sentry/react';
initSentry();

// Auth store — must initialise before loading data
import { authActions, authState } from './stores/authStore';
import { loadCompanyData } from './utils/loadData';

const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

/**
 * Initialise the application:
 * 1. Try to restore the user session from a stored refresh token.
 * 2. If authenticated, load company data.
 */
async function initializeApp() {
  // Step 1: Restore auth session (sets authState.isInitialised = true)
  await authActions.initialise();

  // Step 2: Only load company data if the user is authenticated
  if (authState.user) {
    loadCompanyData();
  }
}

// Start initialization (non-blocking — UI renders immediately)
initializeApp();

function ErrorFallback() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', padding: '2rem', textAlign: 'center', flexDirection: 'column',
      fontFamily: 'system-ui, sans-serif', color: '#1f2937', background: '#f9fafb',
    }}>
      <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Something went wrong</h1>
      <p style={{ color: '#6b7280', maxWidth: '32rem', marginBottom: '1.5rem' }}>
        An unexpected error has been reported. Try refreshing the page — if the problem persists, contact your administrator.
      </p>
      <button
        onClick={() => window.location.reload()}
        style={{
          padding: '0.625rem 1.25rem', borderRadius: '0.5rem', border: 0,
          background: 'var(--color-accent)', color: '#fff', cursor: 'pointer', fontWeight: 500,
        }}
      >
        Reload
      </button>
    </div>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <RouterProvider router={router} />
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
